import { useState } from 'react'
import { Box, Text, Group, Stack, UnstyledButton, Collapse } from '@mantine/core'
import { IconCheck, IconMinus, IconChevronDown } from '@tabler/icons-react'
import { StatusMeter } from '../common/StatusMeter'
import { EvidenceQuote } from './EvidenceQuote'

/**
 * Learning objective progress display.
 * Supports both legacy shape {name, progress, status, evidence[]}
 * and new story debrief shape {name, assessment, evidence (string)}.
 */
export function ObjectiveProgress({ objective }) {
  const [expanded, setExpanded] = useState(false)

  const text = objective.objective || objective.name || 'Objective'

  // New shape: assessment string + evidence citation string
  const assessment = objective.assessment
  const evidenceCitation = typeof objective.evidence === 'string' ? objective.evidence : null

  // Legacy shape: progress bar + evidence array
  const progress = objective.progress || 0
  const met = objective.met ?? (objective.status === 'met')
  const evidenceArray = Array.isArray(objective.evidence) ? objective.evidence : []

  const isNewShape = !!assessment
  const statusColor = met ? 'cyan' : progress >= 50 ? 'orange' : 'red'
  const StatusIcon = met ? IconCheck : IconMinus

  return (
    <Box
      style={{
        padding: '12px 16px',
        borderRadius: 8,
        background: isNewShape
          ? 'rgba(0, 229, 255, 0.03)'
          : met
            ? 'rgba(0, 229, 255, 0.04)'
            : 'rgba(255, 145, 0, 0.03)',
        border: `1px solid ${isNewShape ? 'rgba(0, 229, 255, 0.1)' : met ? 'rgba(0, 229, 255, 0.12)' : 'rgba(255, 145, 0, 0.1)'}`,
        transition: 'border-color 0.2s ease',
      }}
    >
      <Stack gap="sm">
        <Group gap="sm" align="flex-start" wrap="nowrap">
          {!isNewShape && (
            <Box
              style={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: met
                  ? 'rgba(0, 229, 255, 0.15)'
                  : 'rgba(255, 145, 0, 0.12)',
                flexShrink: 0,
                marginTop: 2,
                boxShadow: met ? '0 0 8px rgba(0, 229, 255, 0.2)' : 'none',
              }}
            >
              <StatusIcon
                size={14}
                color={met ? 'var(--mantine-color-cyan-4)' : 'var(--mantine-color-orange-4)'}
              />
            </Box>
          )}

          <Box style={{ flex: 1 }}>
            <Text size="sm" fw={600} c={isNewShape ? 'cyan.3' : met ? 'cyan.2' : 'gray.3'} lh={1.4}>
              {text}
            </Text>

            {/* New shape: AI assessment + evidence citation */}
            {isNewShape && (
              <Stack gap="xs" mt={6}>
                <Text size="sm" c="gray.4" lh={1.6}>
                  {assessment}
                </Text>
                {evidenceCitation && (
                  <Text size="xs" c="dimmed" fs="italic" lh={1.4}>
                    Evidence: {evidenceCitation}
                  </Text>
                )}
              </Stack>
            )}

            {/* Legacy shape: progress bar */}
            {!isNewShape && (
              <Box mt={6}>
                <StatusMeter value={progress} color={statusColor} />
              </Box>
            )}
          </Box>
        </Group>

        {/* Legacy shape: expandable evidence array */}
        {evidenceArray.length > 0 && (
          <>
            <UnstyledButton
              onClick={() => setExpanded((v) => !v)}
              style={{ width: '100%' }}
            >
              <Group gap={4} justify="center">
                <Text size="xs" c="dimmed" fw={600}>
                  {expanded ? 'Hide' : 'View'} Evidence ({evidenceArray.length})
                </Text>
                <IconChevronDown
                  size={14}
                  color="var(--mantine-color-dimmed)"
                  style={{
                    transform: expanded ? 'rotate(180deg)' : 'rotate(0)',
                    transition: 'transform 0.2s ease',
                  }}
                />
              </Group>
            </UnstyledButton>

            <Collapse in={expanded}>
              <Stack gap={0} mt={4}>
                {evidenceArray.map((ev, i) => (
                  <EvidenceQuote key={i} evidence={ev} />
                ))}
              </Stack>
            </Collapse>
          </>
        )}
      </Stack>
    </Box>
  )
}
